import { type Required } from 'utility-types';
import { type z } from 'zod';

import { omit } from '@craft/lib/primitives';

import {
  addQueryParamsToUrl,
  type QueryParamObj,
  removeLeadingSlashesFromPath,
  removeTrailingSlashesFromPath,
} from '@/src/lib/urls';

import * as errors from '../errors';
import { getHeadersEntries, mergeHeaders } from '../headers';
import * as types from '../types';

const isOnlySlashes = (value: string) => {
  for (const char of value) {
    if (char !== '/') {
      return false;
    }
  }
  return true;
};

const DefaultDynamicOptions: Record<types.HttpMethod, Required<ClientDynamicRequestOptions>> = {
  [types.HttpMethod.Delete]: { json: false, strict: false },
  [types.HttpMethod.Get]: { json: true, strict: false },
  [types.HttpMethod.Patch]: { json: true, strict: false },
  [types.HttpMethod.Post]: { json: true, strict: false },
  [types.HttpMethod.Put]: { json: true, strict: false },
};

export type ResponseMeta = {
  readonly method: types.HttpMethod;
  readonly status: number;
  readonly url: string;
};

/**
 * Options that are dynamically provided when a requesting method on the {@link HttpClient}  is
 * called, excluding options that are native to the fetch API's {@link Request} object.
 *
 * These options are used by the {@link HttpClient} to determine the manner in which the method
 * returns.
 */
export type ClientDynamicRequestOptions<
  S extends boolean = boolean,
  J extends boolean = boolean,
> = {
  /**
   * Controls whether or not the requesting method on the {@link HttpClient} instance should
   * return the {@link Response} object itself, or the parsed JSON body of the {@link Response}.
   */
  readonly json?: J;
  /**
   * Controls whether or not the requesting method on the {@link HttpClient} instance should throw
   * an {@link HttpError} in the case that the request fails, or if the requesting method on the
   * {@link HttpClient} should instead simply include the error as a part of the requesting
   * method's return.
   */
  readonly strict?: S;
};

/**
 * Options for each request that are provided when the requesting method is called on the
 * {@link HttpClient}.  This includes both native options for the {@link Request} object, and
 * additional options that are used by the {@link HttpClient} instance,
 * {@link ClientDynamicRequestOptions}.
 */
export type ClientRequestOptions<
  S extends boolean = boolean,
  J extends boolean = boolean,
> = ClientDynamicRequestOptions<S, J> & Omit<RequestInit, 'body' | 'method'>;

type ClientRequestOptionsWithSchema<
  Z extends z.ZodType = never,
  S extends boolean = boolean,
  J extends boolean = boolean,
> = {
  readonly schema?: Z;
} & ClientRequestOptions<S, J>;

export type ClientResponseOrError<T> =
  | {
      readonly error: errors.HttpError;
      readonly meta?: never;
      readonly response?: never;
    }
  | { readonly error?: never; readonly meta: ResponseMeta; readonly response: NonNullable<T> };

export type ClientResponse<T> = ClientResponseOrError<T> | T;

export type HttpClientConfig = {
  readonly baseUrl?: string;
} & RequestInit;

/**
 * A standardized interface that should be used to make HTTP requests using the native {@link fetch}
 * API.  The {@link HttpClient} is designed to improve usage and consistency of error handling when
 * dealing with HTTP requests.
 */
export class HttpClient {
  private readonly config: HttpClientConfig;

  constructor(config?: HttpClientConfig) {
    this.config = config ?? {};
  }

  private static getDynamicOption(
    optionName: keyof ClientDynamicRequestOptions,
    method: types.HttpMethod,
    options: ClientDynamicRequestOptions = {},
  ): boolean {
    return options[optionName] ?? DefaultDynamicOptions[method][optionName];
  }

  public static prependBase({
    baseUrl,
    path,
  }: {
    readonly baseUrl?: null | string;
    readonly path: string;
  }): string {
    if (baseUrl) {
      const sanitized = HttpClient.sanitizeBaseUrl(baseUrl);
      if (sanitized && !path.startsWith(sanitized)) {
        return `${removeTrailingSlashesFromPath(sanitized)}/${removeLeadingSlashesFromPath(path)}`;
      }
    }
    return path;
  }

  public static sanitizeBaseUrl(baseUrl: null | string | undefined) {
    if (baseUrl) {
      const trimmed = baseUrl.trim();
      if (trimmed.length !== 0 && !isOnlySlashes(trimmed)) {
        return trimmed;
      }
    }
    return null;
  }

  private encodeBody(
    body: FormData | types.JsonObject | undefined | URLSearchParams,
    options?: ClientRequestOptions,
  ) {
    // For GET and HEAD requests, body should always return as `null`
    if (body === undefined) {
      return null;
    }

    const contentType = this.getHeader('Content-Type', options);
    if (body instanceof FormData) {
      if (contentType !== null) {
        throw new Error(
          'Do not manually set Content-Type header for requests where body is type ' + 'FormData.',
        );
      }
      return body;
    } else if (body instanceof URLSearchParams) {
      if (contentType !== 'application/x-www-form-urlencoded') {
        throw new Error(
          'The Content-Type header must be set to "application/x-www-form-urlencoded" when ' +
            'the body is an instance of URLSearchParams.',
        );
      }
      return body;
    }

    return JSON.stringify(body);
  }

  private getHeader(name: string, options?: ClientRequestOptions): null | string {
    const headers = mergeHeaders(this.config.headers ?? new Headers(), options?.headers);
    const entries = getHeadersEntries(headers);
    return entries.find(([key]) => key.toLowerCase() === name.toLowerCase())?.[1] ?? null;
  }

  private handleError(
    error: errors.HttpError,
    method: types.HttpMethod,
    options?: ClientDynamicRequestOptions,
  ) {
    if (HttpClient.getDynamicOption('strict', method, options)) {
      throw error;
    }
    return { error };
  }

  private handleResponse(
    data: unknown,
    response: Response,
    request: Request,
    method: types.HttpMethod,
    options?: ClientDynamicRequestOptions,
  ) {
    if (HttpClient.getDynamicOption('strict', method, options)) {
      return data;
    }
    return {
      meta: {
        method: request.method.toUpperCase() as types.HttpMethod,
        status: response.status,
        url: request.url,
      },
      response: data,
    };
  }

  private async request<Z extends z.ZodType>(
    path: string,
    method: types.HttpMethod,
    {
      body,
      json,
      schema,
      strict,
      ...requestOptions
    }: {
      readonly body?: FormData | types.JsonObject | URLSearchParams;
    } & ClientRequestOptionsWithSchema<Z>,
  ): Promise<ClientResponse<unknown>> {
    let response: null | Response = null;

    const url = this.constructUrl(path);
    const request = new Request(url, {
      ...this.staticRequestOptions,
      ...requestOptions,
      body: this.encodeBody(body, requestOptions),
      headers: mergeHeaders(
        this.staticRequestOptions.headers ?? new Headers(),
        requestOptions.headers,
      ),
      method,
    });
    try {
      response = await fetch(request);
    } catch (e) {
      if (!(e instanceof Error)) {
        throw new Error(`Fetch API unexpectedly threw non-Error object: ${JSON.stringify(e)}`);
      }
      const error = new errors.HttpNetworkError(e, {
        method: request.method.toUpperCase() as types.HttpMethod,
        url: request.url,
      });
      return this.handleError(error, method, { json, strict });
    }

    if (!response.ok) {
      const error = new errors.HttpClientError({
        method: request.method.toUpperCase() as types.HttpMethod,
        response,
        status: response.status,
        url: request.url,
      });
      return this.handleError(error, method, { json, strict });
    }

    if (HttpClient.getDynamicOption('json', method, { json, strict })) {
      let jsonResponse: unknown;
      try {
        jsonResponse = await response.json();
      } catch {
        const error = new errors.HttpSerializationError({
          method: request.method.toUpperCase() as types.HttpMethod,
          response,
          status: response.status,
          url: request.url,
        });
        return this.handleError(error, method, { json, strict });
      }
      if (schema) {
        const parsed = schema.safeParse(jsonResponse);
        if (parsed.error) {
          const error = new errors.HttpParseError({
            error: parsed.error,
            method: request.method.toUpperCase() as types.HttpMethod,
            response,
            status: response.status,
            url: request.url,
          });
          return this.handleError(error, method, { json, strict });
        }
        return this.handleResponse(parsed.data, response, request, method, { json, strict });
      }
      return this.handleResponse(jsonResponse, response, request, method, { json, strict });
    }
    return this.handleResponse(response, response, request, method, { json, strict });
  }

  public constructUrl(path: string): string {
    return HttpClient.prependBase({ baseUrl: this.baseUrl, path });
  }

  public async delete(
    path: string,
    options: Required<ClientRequestOptions<true, true>, keyof ClientDynamicRequestOptions>,
  ): Promise<unknown>;

  public async delete<Z extends z.ZodType>(
    path: string,
    options: Required<
      ClientRequestOptionsWithSchema<Z, true, true>,
      keyof ClientDynamicRequestOptions
    >,
  ): Promise<z.infer<Z>>;

  public async delete(
    path: string,
    options: Required<ClientRequestOptions<false, true>, 'json'>,
  ): Promise<ClientResponseOrError<unknown>>;

  public async delete<Z extends z.ZodType>(
    path: string,
    options: Required<ClientRequestOptionsWithSchema<Z, false, true>, 'json'>,
  ): Promise<ClientResponseOrError<z.infer<Z>>>;

  public async delete(
    path: string,
    options: Required<ClientRequestOptions<true, false>, 'strict'>,
  ): Promise<Response>;

  public async delete(
    path: string,
    options?: ClientRequestOptions<false, false>,
  ): Promise<ClientResponseOrError<Response>>;

  /**
   * Sends a DELETE request to the provided path or URL, {@link string}.
   *
   * @param {string} path The path or URL to send the request.
   * @param {ClientRequestOptions} options
   *   The options for the request.  These options will override any options that were provided
   *   during the configuration of the {@link HttpClient} instance.
   *
   *   Unlike the other HTTP methods attached to the {@link HttpClient}, for a DELETE request, the
   *   default value of the `processed` option is false.
   *
   * @returns {Promise<ClientResponse>}
   *   A {@link Promise} that either resolves to an object that contains either the response or an
   *   error, or the response itself, where the response is either the native fetch response object,
   *   {@link Response}, or the parsed JSON body of the response - depending on the options that
   *   are dynamically provided to this method, {@link ClientDynamicRequestOptions}.
   */
  public async delete<Z extends z.ZodType>(
    path: string,
    options?: ClientRequestOptions | ClientRequestOptionsWithSchema<Z>,
  ) {
    return await this.request(path, types.HttpMethod.Delete, options ?? {});
  }

  public async get(
    path: string,
    query: QueryParamObj | undefined,
    options: Required<ClientRequestOptions<true, true>, 'strict'>,
  ): Promise<unknown>;

  public async get<Z extends z.ZodType>(
    path: string,
    query: QueryParamObj | undefined,
    options: Required<ClientRequestOptionsWithSchema<Z, true, true>, 'strict'>,
  ): Promise<z.infer<Z>>;

  public async get(
    path: string,
    query?: QueryParamObj,
    options?: ClientRequestOptions<false, true>,
  ): Promise<ClientResponseOrError<unknown>>;

  public async get<Z extends z.ZodType>(
    path: string,
    query?: QueryParamObj,
    options?: ClientRequestOptionsWithSchema<Z, false, true>,
  ): Promise<ClientResponseOrError<z.infer<Z>>>;

  public async get(
    path: string,
    query: QueryParamObj,
    options: Required<ClientRequestOptions<true, false>, keyof ClientDynamicRequestOptions>,
  ): Promise<Response>;

  public async get(
    path: string,
    query?: QueryParamObj,
    options?: Required<ClientRequestOptions<false, false>, 'json'>,
  ): Promise<ClientResponseOrError<Response>>;

  /**
   * Sends a GET request to the provided path or URL, {@link string} with the provided query,
   * {@link types.RawQuery}.
   *
   * @param {string} path The path to send the request.
   * @param {QueryParamObj} query The query parameters that should be embedded in the URL.
   * @param {ClientRequestOptions} options
   *   The options for the request.  These options will override any options that were provided
   *   during the configuration of the {@link HttpClient} instance.
   *
   * @returns {Promise<ClientResponse>}
   *   A {@link Promise} that either resolves to an object that contains either the response or an
   *   error, or the response itself, where the response is either the native fetch response object,
   *   {@link Response}, or the parsed JSON body of the response - depending on the options that
   *   are dynamically provided to this method, {@link ClientDynamicRequestOptions}.
   */
  public async get<Z extends z.ZodType>(
    path: string,
    query?: QueryParamObj,
    options?: ClientRequestOptions | ClientRequestOptionsWithSchema<Z>,
  ) {
    const pathWithQuery = addQueryParamsToUrl(path, query);
    return await this.request(pathWithQuery, types.HttpMethod.Get, options ?? {});
  }

  public hasBaseUrl(): boolean {
    return this.baseUrl !== null;
  }

  public async patch(
    path: string,
    body: FormData | types.JsonObject | undefined | URLSearchParams,
    options: Required<ClientRequestOptions<true, true>, 'strict'>,
  ): Promise<unknown>;

  public async patch<Z extends z.ZodType>(
    path: string,
    body: FormData | types.JsonObject | undefined | URLSearchParams,
    options: Required<ClientRequestOptionsWithSchema<Z, true, true>, 'strict'>,
  ): Promise<z.infer<Z>>;

  public async patch(
    path: string,
    body?: FormData | types.JsonObject | URLSearchParams,
    options?: ClientRequestOptions<false, true>,
  ): Promise<ClientResponseOrError<unknown>>;

  public async patch<Z extends z.ZodType>(
    path: string,
    body?: FormData | types.JsonObject | URLSearchParams,
    options?: ClientRequestOptionsWithSchema<Z, false, true>,
  ): Promise<ClientResponseOrError<z.infer<Z>>>;

  public async patch(
    path: string,
    body: FormData | types.JsonObject | undefined | URLSearchParams,
    options: Required<ClientRequestOptions<true, false>, keyof ClientDynamicRequestOptions>,
  ): Promise<Response>;

  public async patch(
    path: string,
    body: FormData | types.JsonObject | undefined | URLSearchParams,
    options: Required<ClientRequestOptions<false, false>, 'json'>,
  ): Promise<ClientResponseOrError<Response>>;

  /**
   * Sends a PATCH request to the provided path or URL, {@link string} with the provided body,
   * {@link types.JsonObject}.
   *
   * @param {string} path The path to send the request.
   * @param {FormData | types.JsonObject | URLSearchParams} body
   *   The body that should be attached to the request.
   * @param {ClientRequestOptions} options
   *   The options for the request.  These options will override any options that were provided
   *   during the configuration of the {@link HttpClient} instance.
   *
   * @returns {Promise<ClientResponse>}
   *   A {@link Promise} that either resolves to an object that contains either the response or an
   *   error, or the response itself, where the response is either the native fetch response object,
   *   {@link Response}, or the parsed JSON body of the response - depending on the options that
   *   are dynamically provided to this method, {@link ClientDynamicRequestOptions}.
   */
  public async patch<Z extends z.ZodType>(
    path: string,
    body?: FormData | types.JsonObject | URLSearchParams,
    options?: ClientRequestOptions | ClientRequestOptionsWithSchema<Z>,
  ) {
    return await this.request(path, types.HttpMethod.Patch, {
      ...options,
      body,
    });
  }

  public async post(
    path: string,
    body: FormData | types.JsonObject | undefined | URLSearchParams,
    options: Required<ClientRequestOptions<true, true>, 'strict'>,
  ): Promise<unknown>;

  public async post<Z extends z.ZodType>(
    path: string,
    body: FormData | types.JsonObject | undefined | URLSearchParams,
    options: Required<ClientRequestOptionsWithSchema<Z, true, true>, 'strict'>,
  ): Promise<z.infer<Z>>;

  public async post(
    path: string,
    body?: FormData | types.JsonObject | URLSearchParams,
    options?: ClientRequestOptions<false, true>,
  ): Promise<ClientResponseOrError<unknown>>;

  public async post<Z extends z.ZodType>(
    path: string,
    body?: FormData | types.JsonObject | URLSearchParams,
    options?: ClientRequestOptionsWithSchema<Z, false, true>,
  ): Promise<ClientResponseOrError<z.infer<Z>>>;

  public async post(
    path: string,
    body: FormData | types.JsonObject | undefined | URLSearchParams,
    options: Required<ClientRequestOptions<true, false>, keyof ClientDynamicRequestOptions>,
  ): Promise<Response>;

  public async post(
    path: string,
    body: FormData | types.JsonObject | undefined | URLSearchParams,
    options: Required<ClientRequestOptions<false, false>, 'json'>,
  ): Promise<ClientResponseOrError<Response>>;

  /**
   * Sends a POST request to the provided path or URL, {@link string}, with the provided body,
   * {@link types.JsonObject}.
   *
   * @param {string} path The path to send the request.
   * @param {FormData | types.JsonObject | URLSearchParams} body
   *   The body that should be attached to the request.
   * @param {ClientRequestOptions} options
   *   The options for the request.  These options will override any options that were provided
   *   during the configuration of the {@link HttpClient} instance.
   *
   * @returns {Promise<ClientResponse>}
   *   A {@link Promise} that either resolves to an object that contains either the response or an
   *   error, or the response itself, where the response is either the native fetch response object,
   *   {@link Response}, or the parsed JSON body of the response - depending on the options that
   *   are dynamically provided to this method, {@link ClientDynamicRequestOptions}.
   */
  public async post<Z extends z.ZodType>(
    path: string,
    body?: FormData | types.JsonObject | URLSearchParams,
    options?: ClientRequestOptions | ClientRequestOptionsWithSchema<Z>,
  ) {
    return await this.request(path, types.HttpMethod.Post, {
      ...options,
      body,
    });
  }

  public async put(
    path: string,
    body: FormData | types.JsonObject | undefined | URLSearchParams,
    options: Required<ClientRequestOptions<true, true>, 'strict'>,
  ): Promise<unknown>;

  public async put<Z extends z.ZodType>(
    path: string,
    body: FormData | types.JsonObject | undefined | URLSearchParams,
    options: Required<ClientRequestOptionsWithSchema<Z, true, true>, 'strict'>,
  ): Promise<z.infer<Z>>;

  public async put(
    path: string,
    body?: FormData | types.JsonObject | URLSearchParams,
    options?: ClientRequestOptions<false, true>,
  ): Promise<ClientResponseOrError<unknown>>;

  public async put<Z extends z.ZodType>(
    path: string,
    body?: FormData | types.JsonObject | URLSearchParams,
    options?: ClientRequestOptionsWithSchema<Z, false, true>,
  ): Promise<ClientResponseOrError<z.infer<Z>>>;

  public async put(
    path: string,
    body: FormData | types.JsonObject | undefined | URLSearchParams,
    options: Required<ClientRequestOptions<true, false>, keyof ClientDynamicRequestOptions>,
  ): Promise<Response>;

  public async put(
    path: string,
    body: FormData | types.JsonObject | undefined | URLSearchParams,
    options: Required<ClientRequestOptions<false, false>, 'json'>,
  ): Promise<ClientResponseOrError<Response>>;

  /**
   * Sends a put request to the provided path or URL, {@link string}, with the provided body,
   * {@link types.JsonObject}.
   *
   * @param {string} path The path to send the request.
   * @param {FormData | types.JsonObject | URLSearchParams} body
   *   The body that should be attached to the request.
   * @param {ClientRequestOptions} options
   *   The options for the request.  These options will override any options that were provided
   *   during the configuration of the {@link HttpClient} instance.
   *
   * @returns {Promise<ClientResponse>}
   *   A {@link Promise} that either resolves to an object that contains either the response or an
   *   error, or the response itself, where the response is either the native fetch response object,
   *   {@link Response}, or the parsed JSON body of the response - depending on the options that
   *   are dynamically provided to this method, {@link ClientDynamicRequestOptions}.
   */
  public async put<Z extends z.ZodType>(
    path: string,
    body?: FormData | types.JsonObject | URLSearchParams,
    options?: ClientRequestOptions | ClientRequestOptionsWithSchema<Z>,
  ) {
    return await this.request(path, types.HttpMethod.Put, {
      ...options,
      body,
    });
  }

  public get baseUrl(): null | string {
    return HttpClient.sanitizeBaseUrl(this.config.baseUrl);
  }

  private get staticRequestOptions(): RequestInit {
    return omit(this.config, 'baseUrl');
  }
}
